// Requests started early for the next page, taken once by it.
//
// "Phone verified" counts down before step 4 (autoContinue), and every authed call costs about
// two seconds on integration. The phone page starts step 4's calls during that count, and step 4
// takes the answers instead of asking again: the same requests, only earlier, so it opens ready
// rather than on its skeleton. Nothing here changes what the server is asked. (The breakdown
// check is kept a minute instead, for every step: common/breakdownInfo.)
//
// An entry is used once and only while fresh. One that failed, or nobody took, is simply asked
// again by its page, as if it had never been started.

const MAX_AGE_MS = 60 * 1000;

interface Entry {
  at: number;
  promise: Promise<any>;
}

const entries: { [key: string]: Entry } = {};

/** Starts `load` now under `key`, unless a fresh one is already on its way. */
export const prefetch = <T>(key: string, load: () => Promise<T>): Promise<T> => {
  const current = entries[key];
  if (current && Date.now() - current.at <= MAX_AGE_MS) return current.promise;
  const promise = load();
  // Nobody may ever take it; a failure must not surface as an unhandled rejection.
  promise.catch(() => undefined);
  entries[key] = { at: Date.now(), promise };
  return promise;
};

/** The prefetched answer for `key` (used up), else `load()`; a failed prefetch also asks again. */
export const prefetched = <T>(key: string, load: () => Promise<T>): Promise<T> => {
  const entry = entries[key];
  delete entries[key];
  if (!entry || Date.now() - entry.at > MAX_AGE_MS) return load();
  return entry.promise.catch(() => load());
};

export const reviewKey = (submissionId: number) => `review:${submissionId}`;

/** Tests only. */
export const clearPrefetched = () => {
  Object.keys(entries).forEach((key) => delete entries[key]);
};
